import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Save, X, ArrowLeft, Layers } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";

const PREVIEW_TARGETS = [
  { path: "/", label: "Homepage membres (B2C)" },
  { path: "/pro", label: "Landing commerçants (B2B)" },
  { path: "/devenir-partenaire", label: "Formulaire devenir partenaire" },
  { path: "/admin/login", label: "Page de connexion admin" },
];

/**
 * Floating banner visible on any page while a Brand CMS preview is active.
 * - "Enregistrer" : PATCH /api/admin/config with the current brand (super_admin only).
 * - "Arrêter" : reload /api/config to restore saved config.
 * - "Retour à la config" : navigate back to /admin/configuration.
 * - "Pages" : quick jump between landing pages to preview the brand everywhere.
 */
export default function PreviewBanner() {
  const { brand, previewing, stopPreview, refresh } = useBrand();
  const auth = useAuth();
  const user = auth?.user;
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);

  if (!previewing) return null;

  const isAdmin = user && user.role === "super_admin";

  const handleStop = async () => {
    await stopPreview();
    toast.success("Aperçu arrêté — configuration d'origine rétablie");
  };

  const handleSave = async () => {
    if (!isAdmin) {
      toast.error("Connexion super-admin requise pour enregistrer");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/admin/config", brand);
      await refresh();
      toast.success("Configuration enregistrée définitivement ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="preview-banner"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[9999] w-[min(96vw,780px)]"
    >
      <div
        className="rounded-2xl shadow-2xl border-2 px-3 py-2.5 sm:px-4 sm:py-3 flex items-center gap-2 sm:gap-3 flex-wrap"
        style={{
          background: "#fff",
          borderColor: brand.color_accent || "#BFFF00",
        }}
      >
        <div
          className="flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest whitespace-nowrap"
          style={{ background: brand.color_primary || "#0b1220", color: brand.color_accent || "#BFFF00" }}
        >
          <Eye className="w-3.5 h-3.5" />
          Mode aperçu
        </div>
        <div className="text-xs sm:text-sm text-navy-900/80 flex-1 min-w-[120px] leading-tight">
          Modifications non enregistrées. Naviguez sur le site pour tester.
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            onClick={() => navigate("/admin/configuration")}
            className="text-xs font-semibold px-2.5 py-1.5 rounded-full border border-navy-900/20 hover:bg-navy-900/5 flex items-center gap-1"
            data-testid="preview-back-btn"
            title="Retour à la configuration"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Config</span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="text-xs font-semibold px-2.5 py-1.5 rounded-full border border-navy-900/20 hover:bg-navy-900/5 flex items-center gap-1"
                data-testid="preview-pages-trigger"
                title="Changer de page à prévisualiser"
              >
                <Layers className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Pages</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs">Prévisualiser sur…</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {PREVIEW_TARGETS.map((t) => (
                <DropdownMenuCheckboxItem
                  key={t.path}
                  checked={location.pathname === t.path}
                  onCheckedChange={() => navigate(t.path)}
                  className="cursor-pointer"
                  data-testid={`preview-page-${t.path.replace(/\//g, "-") || "root"}`}
                >
                  <div>
                    <div className="text-sm font-semibold">{t.label}</div>
                    <div className="text-[10px] text-navy-900/50 font-mono">{t.path}</div>
                  </div>
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {isAdmin && (
            <button
              onClick={handleSave}
              disabled={busy}
              className="text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 disabled:opacity-50"
              style={{ background: brand.color_accent || "#BFFF00", color: brand.color_primary || "#0b1220" }}
              data-testid="preview-save-btn"
            >
              <Save className="w-3.5 h-3.5" />
              {busy ? "…" : "Enregistrer"}
            </button>
          )}
          <button
            onClick={handleStop}
            className="text-xs font-semibold px-2.5 py-1.5 rounded-full border border-red-300 text-red-600 hover:bg-red-50 flex items-center gap-1"
            data-testid="preview-stop-btn"
            title="Arrêter l'aperçu"
          >
            <X className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Arrêter</span>
          </button>
        </div>
      </div>
    </div>
  );
}
